import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { animateScroll as scroll } from 'react-scroll/modules';

import { Button, ButtonTypes } from '@eika/button';
import { ErrorCard } from '@eika/card';

import { FormTypeCalculatorLoanInfo, FormTypeCalculatorVehicleInfo } from '../../actions';
import { DispatchWithState, StringKeyAnyValue, StringKeyValue } from '../../actions/types';
import Loader from '../../components/Loader/Loader';
import ProductImage from '../../components/ProductImage/ProductImage';
import customHistory from '../../customHistory';
import { CalculatorReducer } from '../../reducers/calculator';
import { ContextReducer } from '../../reducers/context';
import { LoaderReducer } from '../../reducers/loader';
import { VehicleReducer } from '../../reducers/vehicle';
import { getProductIcon } from '../../util/mappings';
import LoanInfo from './LoanInfo/LoanInfo';
import LoanTypeSelection from './LoanTypeSelection/LoanTypeSelection';
import VehicleInfo from './VehicleInfo/VehicleInfo';

interface Props {
    action: {
        fetchProductInfo: (vehicleURL: string) => DispatchWithState;
        fetchVehicleInfo: (form: FormTypeCalculatorVehicleInfo) => DispatchWithState;
        loanCalculatorNextEvent: (form: FormTypeCalculatorLoanInfo) => DispatchWithState;
        updateLoanType: (loanType: string) => void;
    };
    calculator: CalculatorReducer;
    context: ContextReducer;
    form: StringKeyAnyValue;
    vehicle: VehicleReducer;
    loader: LoaderReducer;
    text: StringKeyValue;
}

const LoanCalculator = (props: Props): JSX.Element => {
    useEffect(() => {
        scroll.scrollToTop();
    }, []);

    const { id } = useParams<'id'>();
    const { action, loader, calculator, vehicle, context, text, form } = props;
    const { fetchProductInfo, updateLoanType } = action;
    const { isFetchingProductInfo, fetchProductInfoFailed, hasFetchedVehicleInfo } = loader;
    const { loanInfo, isLoan, isLoanCommitment, isCarElectric, isObjectAgeEligible, isObjectZeroEmission } = calculator;
    const { loanType, loanProduct } = loanInfo;

    // Fetch Product Info!
    useEffect(() => {
        if (id) fetchProductInfo(id);
    }, [id, fetchProductInfo]);

    if (isFetchingProductInfo) return <Loader title={text.loadingProducts} />;

    if (fetchProductInfoFailed) {
        return (
            <ErrorCard title="Noe gikk galt!" description={text.calculatorInvalidProductUrl}>
                <Button buttonType={ButtonTypes.STANDARD_POSITIVE} onClick={() => customHistory.push('/')}>
                    {text.vehicleTypeSelectorChooseVehicle}
                </Button>
            </ErrorCard>
        );
    }

    return (
        <div className="loan-calculator">
            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                {/* Product Image */}
                <ProductImage
                    className="loan-calculator__intro"
                    image={getProductIcon(loanType, loanProduct)}
                    title={text.calculatorTitle}
                />

                {/* Select Loan Type (Loan or Loan Commitment) */}
                <LoanTypeSelection
                    calculator={calculator}
                    text={text}
                    selected={loanType || ''}
                    onChange={updateLoanType}
                />

                {/* Vehicle Info (Vehicle Search / Info) */}
                {isLoan && (
                    <VehicleInfo
                        action={action}
                        calculator={calculator}
                        vehicle={vehicle}
                        loader={loader}
                        text={text}
                    />
                )}

                {/* Loan Details (Equity, Price, etc) */}
                {(isLoanCommitment ||
                    (hasFetchedVehicleInfo &&
                        (isCarElectric ? isObjectZeroEmission && isObjectAgeEligible : isObjectAgeEligible))) && (
                    <LoanInfo action={action} calculator={calculator} context={context} form={form} text={text} />
                )}
            </div>
        </div>
    );
};

export default LoanCalculator;
